import React from "react"
import { ShareMessageFragment } from "~/__generated__/graphql"
import { UserAvatar, BadgeSize } from "~/ui/user-avatar"
import { MessageHeader } from "./message-header"
import { MessageAttachments } from "./message-attachments/message-attachments"
import { QuotedMessage } from "./message-attachments/quoted-message"
import { FormattedAppText } from "~/ui/formatted-app-text"
import { MessageReplies } from "./message-replies"
import { MessageReactions } from "./message-reactions"
import { AppText } from "~/ui/app-text"
import { cn } from "~/common/cn"

const formatTime = (iso: string) => {
  const date = new Date(iso)
  return date
    .toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true })
    .toLowerCase()
}

interface FeedMessageProps {
  message: ShareMessageFragment
  showName?: boolean
  showAvatar?: boolean
  fullWidth?: boolean
  isFirstInGroup?: boolean
  isLastInGroup?: boolean
}

export const FeedMessage: React.FC<FeedMessageProps> = ({
  message,
  showName = true,
  showAvatar = true,
  fullWidth = false,
  isFirstInGroup = true,
  isLastInGroup = true,
}) => {
  const user = message.user
  const displayName = user?.displayOrUsername || "User"
  const hasAttachments = message.attachments?.length > 0
  const hasReactions = (message.reactionGroups?.length ?? 0) > 0
  const hasReplies = message.childMessagesCount > 0

  const bubbleRounding = cn(
    "rounded-r",
    isFirstInGroup && "rounded-tl",
    isLastInGroup && "rounded-bl"
  )

  return (
    <div className="flex flex-row gap-2">
      <div className="flex w-8 shrink-0 flex-col justify-end">
        {showAvatar && (
          <>
            <div className="shrink-0">
              <UserAvatar avatarUrl={user?.avatarUrl} displayName={displayName} size="xsm-sm" />
            </div>
            {(hasReactions || hasReplies) && <div className="shrink-0" style={{ height: 24 }} />}
          </>
        )}
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div
          className={cn(
            fullWidth ? "w-full" : "w-fit max-w-full",
            bubbleRounding,
            "bg-neutral-100 px-3 py-2"
          )}
        >
          <div className="flex flex-col gap-1">
            {showName && (
              <div className="flex items-center justify-between gap-3">
                <MessageHeader
                  showVerified={user?.verifiedAccount}
                  displayName={displayName}
                  badgeSize={BadgeSize.sm}
                />
                <AppText variant="caption" className="shrink-0 text-base-gray">
                  {formatTime(message.createdAt)}
                </AppText>
              </div>
            )}
            {!!message.quotedMessage && <QuotedMessage quotedMessage={message.quotedMessage} />}
            {message?.textContent && (
              <FormattedAppText
                text={message.textContent}
                variant="body2"
                className={hasAttachments ? "line-clamp-2" : "line-clamp-10"}
              />
            )}
            {hasAttachments && (
              <div className="overflow-hidden rounded-xl">
                <MessageAttachments attachments={message.attachments} />
              </div>
            )}
          </div>
        </div>
        {(hasReplies || hasReactions) && (
          <div className="flex flex-wrap gap-1">
            {hasReplies && message.childMessageUsers && (
              <MessageReplies
                childMessagesCount={message.childMessagesCount}
                childMessageUsers={message.childMessageUsers}
              />
            )}
            <MessageReactions reactionGroups={message.reactionGroups} />
          </div>
        )}
      </div>
    </div>
  )
}
